import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { ActionEntityType } from '@pmp/api';
import { ExternalFiltersUrlCodec, hasAnyParam, LIST_URL_SPECS, writeExternalFilterParams } from '@pmp/utils';

const buildStorageKey = (entityType: ActionEntityType) => `${entityType}ExternalFilters`;

const readStoredFilters = <T>(entityType: ActionEntityType): T | null => {
  try {
    const raw = localStorage.getItem(buildStorageKey(entityType));
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
};

/**
 * One list's filters that live outside the grid, remembered per entity.
 *
 * With a `urlCodec` they also travel in the query string, so the list's whole filter state is
 * shareable: a URL that carries any of the codec's params wins over what was remembered, and later
 * changes are written back.
 */
export const useEntityExternalFilters = <T>(entityType: ActionEntityType, defaults: T, urlCodec?: ExternalFiltersUrlCodec<T>) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // The registry owns the param names, so the codec, the list and the agent cannot name them differently.
  const urlParams = useMemo(() => LIST_URL_SPECS[entityType]?.params ?? [], [entityType]);

  const [filters, setFilters] = useState<T>(() => {
    if (urlCodec && hasAnyParam(searchParams, urlParams)) return urlCodec.fromParams(searchParams, defaults);

    return readStoredFilters<T>(entityType) ?? defaults;
  });

  useEffect(() => {
    localStorage.setItem(buildStorageKey(entityType), JSON.stringify(filters));
  }, [entityType, filters]);

  const params = useMemo(() => (urlCodec ? urlCodec.toParams(filters) : undefined), [filters, urlCodec]);

  // Held in refs, synced before the two effects below run, so each depends on its own trigger alone:
  // the writer on the filters changing, the reader on the location changing. A writer that also reran
  // on a location change would push this render's filters back over a URL just navigated to, and a
  // reader that also reran on a filter change would read a URL the writer has not published yet.
  const searchParamsRef = useRef(searchParams);
  const setSearchParamsRef = useRef(setSearchParams);
  const paramsRef = useRef(params);

  useEffect(() => {
    searchParamsRef.current = searchParams;
    setSearchParamsRef.current = setSearchParams;
    paramsRef.current = params;
  });

  // Compared against the live URL first, so a write that would not change anything does not navigate.
  useEffect(() => {
    if (params === undefined) return;

    const current = searchParamsRef.current;
    const next = writeExternalFilterParams(current, params);
    if (next.toString() !== current.toString()) setSearchParamsRef.current(next, { replace: true });
  }, [params]);

  // Back, forward, or a link opened while this list is already mounted.
  useEffect(() => {
    if (!urlCodec) return;

    // A URL carrying none of these params does not mean "no filters"; the remembered view stands. It
    // is written back rather than read, so the URL never claims a narrower set than the grid shows.
    // `replace`, so this does not add a history entry the back button then has to walk through.
    if (!hasAnyParam(searchParams, urlParams)) {
      const written = paramsRef.current;
      if (written === undefined) return;

      const next = writeExternalFilterParams(searchParams, written);
      if (next.toString() !== searchParams.toString()) setSearchParamsRef.current(next, { replace: true });
      return;
    }

    const fromUrl = urlCodec.fromParams(searchParams, defaults);
    setFilters(previous =>
      JSON.stringify(urlCodec.toParams(previous)) === JSON.stringify(urlCodec.toParams(fromUrl)) ? previous : fromUrl,
    );
  }, [defaults, searchParams, urlCodec, urlParams]);

  const resetFilters = useCallback(() => setFilters(defaults), [defaults]);

  return [filters, setFilters, resetFilters] as const;
};
